import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Changing this clears the error — the route path, so navigating away recovers. */
  resetKey?: string;
  /** A page-level boundary keeps the navigation; the outer one has nothing left to keep. */
  variant?: 'page' | 'app';
}

interface State {
  error: Error | null;
}

/**
 * Without this, one component throwing renders a blank white page and the person is left with
 * nothing — no navigation, no message, no idea whether their work saved. React unmounts the
 * whole tree on an uncaught render error, so something has to catch it.
 *
 * Two of these are in use: one around the page inside the shell, so a broken page leaves the
 * sidebar working and the rest of the app reachable, and one around everything for the case
 * where the shell itself is what broke.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is all we have until there is somewhere to send these.
    console.error('Unhandled error in the interface', error, info.componentStack);
  }

  componentDidUpdate(previous: Props): void {
    // Navigating to another page is the most natural way out, so let it clear the error.
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const pageLevel = this.props.variant !== 'app';

    return (
      <div className={pageLevel ? 'main-inner' : 'main-inner'} style={pageLevel ? undefined : { padding: 40 }}>
        <div className="card" style={{ maxWidth: 620 }}>
          <div className="card-head">
            <h2>This page could not be shown</h2>
          </div>
          <div className="card-pad">
            <p className="muted" style={{ marginTop: 0 }}>
              Something in the interface failed rather than anything you did. Nothing you had
              already saved is affected — this is the screen, not your data.
            </p>

            {/* The message is shown deliberately: "something went wrong" helps nobody report it. */}
            <div
              className="card card-pad mono"
              style={{ background: 'var(--surface-sunken)', fontSize: 12, whiteSpace: 'pre-wrap' }}
            >
              {error.message || String(error)}
            </div>

            <div className="row mt-2">
              <button type="button" className="btn btn-primary" onClick={() => this.setState({ error: null })}>
                Try again
              </button>
              <button type="button" className="btn" onClick={() => window.location.reload()}>
                Reload the page
              </button>
              {pageLevel && (
                <a className="btn btn-quiet" href="/">
                  Back to the start
                </a>
              )}
            </div>

            <p className="dim" style={{ fontSize: 12, marginBottom: 0, marginTop: 14 }}>
              If it keeps happening, the full details are in the browser console.
            </p>
          </div>
        </div>
      </div>
    );
  }
}
